// One patient record.
//
// ONE query, not four. `allergies`, `conditions` and `medications` are
// selected-only sub-reads on the BFF's Patient type: each costs an extra RPC,
// but they are issued inside a single GraphQL request and every one of them
// degrades to [] on failure — so a formulary hiccup cannot blank the record's
// demographics.
//
// clinicId is passed so شمارهٔ پرونده is filled in: a file number belongs to a
// (clinic, patient) pair, and GetPatient on its own has no way to know which
// clinic is asking.

import React from 'react';
import { bffQuery } from '../lib/bffClient.js';

export const PATIENT_QUERY = `
  query PatientRecord($id: ID!, $clinicId: ID) {
    patient(id: $id, clinicId: $clinicId) {
      id
      name
      phone
      nationalId
      birthDate
      age
      gender
      maritalStatus
      bloodType
      job
      insurance
      height
      createdAt
      fileNumber
      avatarUrl
      allergies { id allergen reaction severity status createdAt }
      conditions { id code display clinicalStatus verificationStatus onsetDate createdAt }
      medications { id code display status authoredOn dosageInstruction endDate appointmentId }
    }
  }
`;

export function usePatient(id, clinicId) {
  const [patient, setPatient] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    if (!id) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    bffQuery(PATIENT_QUERY, { id, clinicId: clinicId || null })
      .then((data) => {
        if (!alive) return;
        // `null` here is NOT_FOUND, which the screen renders as its own state —
        // it is not an error and must not offer a retry button.
        setPatient(data?.patient || null);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[usePatient] failed:', err?.message);
        setError(err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id, clinicId, nonce]);

  return { patient, loading, error, reload: () => setNonce((n) => n + 1) };
}

export default usePatient;
